import { useState } from "react";
import styled, { ThemeProvider } from "styled-components";
import { motion } from "motion/react";
import { token } from "../../../../theme";
import MSButton from "../../../../components/MSButton/MSButton";
import { WALLPAPERS, getWallpaper } from "../../../../config/wallpapers";
import { useDesktop } from "../../utils/DesktopContext";

interface DisplayPropertiesProps {
  onClose: () => void;
}

// Win98 "Display Properties": pick a wallpaper, preview it on the little monitor.
const DisplayProperties = ({ onClose }: DisplayPropertiesProps) => {
  const { wallpaper, setWallpaper, crtOn, toggleCrt } = useDesktop();
  const [selected, setSelected] = useState(wallpaper);

  return (
    <ThemeProvider theme={token}>
      <Container>
        <MonitorWrap>
          <Monitor>
            <Screen
              key={selected}
              initial={{ opacity: 0.2 }}
              animate={{ opacity: 1 }}
              style={{ background: getWallpaper(selected).background }}
            />
          </Monitor>
          <Stand />
          <Base />
        </MonitorWrap>

        <Label>Wallpaper</Label>
        <ListBox role="listbox">
          {WALLPAPERS.map((option) => (
            <Option
              key={option.id}
              role="option"
              aria-selected={selected === option.id}
              $selected={selected === option.id}
              onClick={() => setSelected(option.id)}
              onDoubleClick={() => {
                setWallpaper(option.id);
                onClose();
              }}
            >
              {option.name}
            </Option>
          ))}
        </ListBox>

        <Checkbox>
          <input type="checkbox" checked={crtOn} onChange={toggleCrt} />
          CRT scanline effect
        </Checkbox>

        <Buttons>
          <MSButton
            onClick={() => {
              setWallpaper(selected);
              onClose();
            }}
          >
            OK
          </MSButton>
          <MSButton onClick={onClose}>Cancel</MSButton>
          <MSButton onClick={() => setWallpaper(selected)}>Apply</MSButton>
        </Buttons>
      </Container>
    </ThemeProvider>
  );
};

export default DisplayProperties;

const Container = styled.div`
  width: 340px;
  padding: 16px;
  font-size: ${(p) => p.theme.fontNormal}px;
`;

const MonitorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
`;

const Monitor = styled.div`
  width: 184px;
  height: 138px;
  padding: 12px 12px 18px;
  background: #c0c0c0;
  border-radius: 6px 6px 2px 2px;
  box-shadow:
    ${(p) => p.theme.standardBoxShadow},
    0 0 0 1px #000;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    right: 12px;
    bottom: 6px;
    width: 6px;
    height: 4px;
    background: #00c000;
  }
`;

const Screen = styled(motion.div)`
  width: 100%;
  height: 100%;
  box-shadow:
    1px 1px 0 0 #000 inset,
    -1px -1px 0 0 #fff inset;
  background-size: cover !important;
`;

const Stand = styled.div`
  width: 40px;
  height: 10px;
  background: #a0a0a0;
  box-shadow:
    1px 0 0 #000,
    -1px 0 0 #000;
`;

const Base = styled.div`
  width: 110px;
  height: 8px;
  background: #c0c0c0;
  box-shadow:
    ${(p) => p.theme.standardBoxShadow},
    0 0 0 1px #000;
`;

const Label = styled.div`
  margin-bottom: 4px;
`;

const ListBox = styled.div`
  background: #fff;
  box-shadow: ${(p) => p.theme.invertedStandardBoxShadow};
  padding: 3px;
  height: 104px;
  overflow-y: auto;
`;

const Option = styled.div<{ $selected: boolean }>`
  padding: 2px 4px;
  cursor: pointer;
  background: ${(p) => (p.$selected ? "#00007b" : "transparent")};
  color: ${(p) => (p.$selected ? "#fff" : "#000")};
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  cursor: pointer;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 16px;

  & > div {
    padding: 8px 16px;
  }
`;
